import * as Yup from 'yup';

import {
   CompositionShareConfig,
   ValidationRoot,
   calculateTotalShare,
   formatCompositionShareError,
} from '../../utils/composition-share.validator';

export const ValidationConfig = {
   messages: {
      share: {
         type: 'Composition share must be a valid number.',
         min: 'Composition share must be at least 0%',
         max: 'Composition share must not exceed 100%',
         total: 'Total composition share must not exceed 100%',
      },
      common: {
         required: 'Required',
         positiveQty: 'Value needs to be greater than 0',
         contentRange: 'Value needs to be between 0 and 100',
         idFormat: 'Value needs to be a 4 digit ID or zero',
         activeFormat: 'Value must be true, false, or * for differing values',
      },
   },
   regex: {
      id: /^[0-9]{4}$|^0$/,
   },
} as const;

const compositionShareSchema = Yup.number()
   .transform((value) => {
      if (typeof value === 'string') {
         const trimmed = value.trim();
         if (trimmed === '') {
            return undefined;
         }
         const parsed = parseFloat(trimmed);
         return isNaN(parsed) ? undefined : parsed;
      }
      return value;
   })
   .typeError(ValidationConfig.messages.share.type)
   .min(CompositionShareConfig.min, ValidationConfig.messages.share.min)
   .max(CompositionShareConfig.max, ValidationConfig.messages.share.max)
   .test('composition-share-sum', ValidationConfig.messages.share.total, function (value: number | undefined) {
      if (this.path.includes('[')) {
         return true;
      }

      const root = this.parent as ValidationRoot;
      if (!root?.parts?.length) {
         return true;
      }

      const result = calculateTotalShare(root.parts, root.parentId || '*', value || 0, root);
      const { total, details } = result;

      if (total > CompositionShareConfig.max + CompositionShareConfig.epsilon) {
         return this.createError({
            message: formatCompositionShareError(total, details),
         });
      }

      return true;
   });

const bulkEditCompositionShareSchema = Yup.mixed()
   .transform((value, originalValue) => {
      if (originalValue === '*') {
         return '*';
      }
      if (typeof value === 'string') {
         const trimmed = value.trim();
         if (trimmed === '') {
            return undefined;
         }
         const parsed = parseFloat(trimmed);
         return isNaN(parsed) ? undefined : parsed;
      }
      return value;
   })
   .test('composition-share-or-asterisk', function (value) {
      if (value === '*') {
         return true;
      }

      if (typeof value !== 'number' && value !== undefined) {
         return this.createError({ message: ValidationConfig.messages.share.type });
      }

      if (value === undefined) {
         return true;
      }

      if (value < CompositionShareConfig.min) {
         return this.createError({ message: ValidationConfig.messages.share.min });
      }

      if (value > CompositionShareConfig.max) {
         return this.createError({ message: ValidationConfig.messages.share.max });
      }

      if (this.path.includes('[')) {
         return true;
      }

      const root = this.parent as ValidationRoot;
      if (!root?.parts?.length) {
         return true;
      }

      const result = calculateTotalShare(root.parts, root.parentId || '*', value, root);
      const { total, details } = result;

      if (total > CompositionShareConfig.max + CompositionShareConfig.epsilon) {
         return this.createError({
            message: formatCompositionShareError(total, details),
         });
      }

      return true;
   });

const baseSchema = {
   part: Yup.string().required(ValidationConfig.messages.common.required).nullable(),
   materialGrade: Yup.string().required(ValidationConfig.messages.common.required).nullable(),
   partDetail: Yup.string().nullable(),
   threeDModelNo: Yup.string().nullable(),
   volume: Yup.string(),
   mass: Yup.string(),
   density: Yup.string(),
   recycledContent: Yup.string().test(
      'isValidRecycledContent',
      ValidationConfig.messages.common.contentRange,
      (value: any, context: any) => {
         const original = context.originalValue;
         if (original === null || original === undefined || original === '') {
            return true;
         }
         const num = Number(original);
         return !isNaN(num) && num >= 0 && num <= 100;
      }
   ),
   renewableContent: Yup.string().test(
      'isValidRenewableContent',
      ValidationConfig.messages.common.contentRange,
      (value: any, context: any) => {
         const original = context.originalValue;
         if (original === null || original === undefined || original === '') {
            return true;
         }
         const num = Number(original);
         return !isNaN(num) && num >= 0 && num <= 100;
      }
   ),
   comment: Yup.string(),
   compositionShare: compositionShareSchema,
   parts: Yup.array()
      .of(
         Yup.object().shape({
            id: Yup.number().required(),
            compositionShare: compositionShareSchema,
            parentId: Yup.string(),
            active: Yup.boolean(),
         })
      )
      .default([]),
   quantity: Yup.string()
      .default('1')
      .test('isValidQty', ValidationConfig.messages.common.positiveQty, (value) => {
         if (!value || value.trim() === '') return false;
         const num = Number(value);
         return !isNaN(num) && num > 0;
      }),
};

const commonSubItemSchema = {
   materialGradeId: Yup.string()
      .transform((value) => (!value || value.trim() === '' ? '0' : value))
      .test('isValidId', ValidationConfig.messages.common.idFormat, (value: any) => value === '0' || /^[0-9]{4}$/.test(value)),
   active: Yup.boolean().default(true),
   locked: Yup.boolean().default(false),
   clientCreated: Yup.boolean().default(true),
};

export const addValidationSchema = Yup.object().shape({
   ...baseSchema,
   id: Yup.number()
      .typeError(ValidationConfig.messages.common.idFormat)
      .test('isValidEntityId', ValidationConfig.messages.common.idFormat, (value) => {
         if (value === undefined) {
            return true;
         }

         return value === 0 || (Number.isInteger(value) && value >= 1000 && value <= 9999);
      }),
   ...commonSubItemSchema,
});

export const editValidationSchema = Yup.object().shape({
   ...baseSchema,
   id: Yup.number().nullable(),
   ...commonSubItemSchema,
});

export const bulkEditValidationSchema = Yup.object().shape({
   part: Yup.string()
      .test('part-valid', ValidationConfig.messages.common.required, function (value) {
         if (value === '*') return true;
         if (!value || value.trim() === '') return false;
         return true;
      })
      .nullable(),

   materialGrade: Yup.string()
      .test('materialGrade-valid', ValidationConfig.messages.common.required, function (value) {
         if (value === '*') return true;
         if (!value || value.trim() === '') return false;
         return true;
      })
      .nullable(),

   partDetail: Yup.string().nullable(),
   threeDModelNo: Yup.string().nullable(),
   volume: Yup.string(),
   mass: Yup.string(),
   density: Yup.string(),

   recycledContent: Yup.string().test(
      'isValidRecycledContent',
      ValidationConfig.messages.common.contentRange,
      (value: any, context: any) => {
         if (value === '*') return true;
         if (!context.originalValue || context.originalValue.trim() === '') return true;
         const num = Number(context.originalValue);
         return !isNaN(num) && num >= 0 && num <= 100;
      }
   ),

   renewableContent: Yup.string().test(
      'isValidRenewableContent',
      ValidationConfig.messages.common.contentRange,
      (value: any, context: any) => {
         if (value === '*') return true;
         if (!context.originalValue || context.originalValue.trim() === '') return true;
         const num = Number(context.originalValue);
         return !isNaN(num) && num >= 0 && num <= 100;
      }
   ),

   comment: Yup.string(),

   compositionShare: bulkEditCompositionShareSchema,

   active: Yup.mixed()
      .transform((value, originalValue) => {
         if (originalValue === '*') return '*';
         return value;
      })
      .test('active-or-asterisk', ValidationConfig.messages.common.activeFormat, (value) => {
         return value === '*' || typeof value === 'boolean' || value === undefined;
      }),

   subItems: Yup.array()
      .of(
         Yup.object().shape({
            id: Yup.number().required(),
            compositionShare: compositionShareSchema,
            parentId: Yup.string(),
            active: Yup.boolean(),
         })
      )
      .default([]),

   parts: Yup.array()
      .of(
         Yup.object().shape({
            id: Yup.number().required(),
            compositionShare: compositionShareSchema,
            parentId: Yup.string(),
            active: Yup.boolean(),
         })
      )
      .default([]),

   quantity: Yup.string().test('isValidQty', ValidationConfig.messages.common.positiveQty, (value) => {
      if (value === '*') return true;
      if (!value || value.trim() === '') return true;
      const num = Number(value);
      return !isNaN(num) && num > 0;
   }),
});
